/**
 * Générateur — Représenter, sur papier ou à l'aide d'un tableur-grapheur, des
 * données sous la forme d'un tableau, d'un diagramme (diagramme en barres,
 * diagramme circulaire) ou d'un graphique cartésien.
 * Attendu ID : 974 | Niveau : 5ème | Domaine : Statistiques (descripteur 131)
 *
 * Version conforme à la spécification 974 (2026-09-21).
 * Ajoutée : « Le relevé de la classe » — le TABLEAU est la quatrième
 * représentation que le programme nomme et le niveau 1 de la graduation
 * (« produire le tableau d'effectifs à partir d'un relevé déjà organisé, en
 * nommant les en-têtes et en calculant le total ») ; aucune carte ne le servait.
 * Ajoutée : « canapes_tableau_vf », qui sert les deux erreurs visées restées
 * sans emploi (angles calculés sur un total faux, légende oubliée).
 * Supprimée : la variante de « sanglier_qcm_unique » qui faisait LIRE le
 * diagramme (« quelle est l'alimentation principale ? ») — c'est l'objectif
 * 973 ; à sa place, le choix du diagramme circulaire réellement produit.
 *
 * Plan de cartes :
 *   « Le relevé de la classe » (niveau 1) — relevé déjà organisé en bâtons,
 *     3 à 6 modalités : l'élève produit le tableau d'effectifs (une case par
 *     modalité plus le total) puis nomme les en-têtes.
 *   « Les précipitations de Brest » (niveaux 2 et 4) — diagramme en bâtons :
 *     placement du sommet de chaque bâton sur un axe dont la consigne annonce
 *     l'unité (20 mm par carreau, niveau 2) ; puis choix de l'échelle et
 *     conversion effectif → carreaux (niveau 4).
 *   « Le stock de canapés » (niveaux 2 et 3) — total divisant 360, type
 *     dominant au-delà de la moitié : chaîne complète de construction du
 *     diagramme circulaire (total, degré par canapé, angles, somme 360°),
 *     choix du diagramme produit (barres ou circulaire), vrai/faux sur le
 *     total des angles et sur la légende.
 *   « Le régime du sanglier » (niveau 3) — pourcentages multiples de 5 :
 *     conversions pourcentage ↔ angle dans les deux sens, choix du diagramme
 *     circulaire produit, vrai/faux dont l'arrondi des pourcentages.
 *   « Les courbes de températures » (niveau 4) — graphique cartésien :
 *     décision sur ce que porte chaque axe, puis placement des points.
 *
 * Spécification appliquée :
 *   Bornes : 3 à 6 catégories (tableau, diagrammes) ; 4 à 8 couples pour un
 *     graphique cartésien (7 jours, 6 mois) ; effectif total pris dans les
 *     diviseurs de 360 de l'intervalle 12..360 ({72, 90, 120, 180}) ; effectif
 *     d'une catégorie ≥ 1 et jamais nul ; angle d'un secteur entier, jamais
 *     inférieur à 10° ; unité de l'axe prise dans {1, 2, 5, 10, 20, 50, 100,
 *     200} de sorte que la plus grande barre occupe 6 à 12 carreaux et la plus
 *     petite au moins 1 ; hauteur d'une barre entre 1 et 12 carreaux.
 *   Exclusions : aucun diagramme semi-circulaire ; aucun histogramme ni
 *     ligne brisée en production (le mot « courbe » a été retiré de la
 *     consigne des températures : on place des points) ; aucun angle non
 *     entier (le total est contraint AVANT le tirage des effectifs) ; les
 *     hauteurs du tracé sur papier quadrillé tombent sur un carreau entier ;
 *     aucune catégorie d'effectif nul ; aucun calcul d'angle détaché d'un
 *     tracé (chaque chaîne d'angles est suivie, dans la même carte, du choix
 *     du diagramme produit).
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`) :
 *     - effectif brut reporté en hauteur au lieu d'être divisé par l'unité → `effectif_brut_en_hauteur`
 *     - angles calculés sur un total faux (catégorie oubliée) → `total_faux_pour_les_angles`
 *     - angle calculé depuis un pourcentage déjà arrondi → `angle_depuis_pourcentage_arrondi`
 *     - tableau produit sans en-têtes ni unité → `tableau_sans_entetes`
 *     - abscisse et ordonnée inversées → `abscisse_ordonnee_inversees`
 *     - légende du diagramme circulaire oubliée → `legende_oubliee`
 *   Deux codes SUPPLÉMENTAIRES, qui ne correspondent à aucune erreur de la
 *     spec et sont signalés comme tels : `echelle_axe_mal_choisie` (le choix
 *     de l'échelle est l'attendu du niveau 4, la spec ne lui associe aucune
 *     erreur visée) et `effectifs_permutes` (un diagramme dont les effectifs
 *     sont permutés, seul distracteur possible d'un QCM d'images).
 *   Graduation → cartes : 1 « Le relevé de la classe » ; 2 « Les précipitations
 *     de Brest » (placement) et « Le stock de canapés » (diagramme en barres) ;
 *     3 « Le stock de canapés » (circulaire) et « Le régime du sanglier » ;
 *     4 « Les courbes de températures » et « Les précipitations de Brest »
 *     (choix de l'échelle).
 *   Non tranché : `support_de_production` nomme « tableur-grapheur », qu'aucun
 *     format TanoEd ne modélise ; aucune carte ne le sert. La tolérance
 *     angulaire des points de vigilance ne s'applique pas : aucun angle n'est
 *     tracé à la main, tous sont saisis comme nombres, donc en égalité stricte.
 */
import {
    randomChoice, randInt, shuffle,
    basePayload, makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { imgElement, imgChoix, svgBarres, svgCamembert, ligneTable, tableEnonce } from './svgStats';

const ATTENDU_ID = 974;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_RELEVE = 'Le relevé de la classe';
const L_TEMP = 'Les courbes de températures';
const L_CANAPES = 'Le stock de canapés';
const L_BREST = 'Les précipitations de Brest';
const L_SANGLIER = 'Le régime du sanglier';

/** Payload placer_points_2d — format canonique STRICT "x;y" (§9bis), entiers ici. */
const makePlacer2D = (texte, repere, points, label, dejaPlaces = null) => {
    const bonne = {};
    points.forEach((p) => { bonne[p.label] = `${p.x};${p.y}`; });
    const contenu = {
        subType: 'placer_points_2d',
        repere,
        pointsToPlace: points.map((p) => p.label),
        bonne_reponse: JSON.stringify(bonne),
    };
    if (dejaPlaces && dejaPlaces.length) contenu.dejaPlaces = dejaPlaces;
    return basePayload('association', texte, contenu, opts(label));
};

/** Garantit qu'un tableau vrai/faux n'est ni tout vrai ni tout faux, sans boucle. */
const equilibrer = (vs) => {
    const nbVrai = vs.filter(Boolean).length;
    if (nbVrai === 0) vs[0] = true;
    else if (nbVrai === vs.length) vs[0] = false;
    return vs;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le relevé de la classe » (niveau 1 — produire le tableau)
// ═════════════════════════════════════════════════════════════

const CARACTERES_RELEVE = [
    { sujet: 'la couleur des yeux', entete: 'Couleur des yeux', effLabel: 'Effectif (nombre d\'élèves)',
        modalites: ['marron', 'bleus', 'verts', 'noisette', 'gris', 'noirs'] },
    { sujet: 'le moyen de transport pour venir au collège', entete: 'Moyen de transport', effLabel: 'Effectif (nombre d\'élèves)',
        modalites: ['à pied', 'en bus', 'à vélo', 'en voiture', 'en trottinette', 'en train'] },
    { sujet: 'le sport pratiqué en club', entete: 'Sport pratiqué', effLabel: 'Effectif (nombre d\'élèves)',
        modalites: ['football', 'natation', 'judo', 'danse', 'tennis', 'handball'] },
];

/** 3 à 6 modalités, effectif de chacune entre 1 et 9 (aucune catégorie vide). */
const tirerReleve = () => {
    const car = randomChoice(CARACTERES_RELEVE);
    const k = randInt(3, 6);
    const modalites = car.modalites.slice(0, k);
    const effs = modalites.map(() => randInt(1, 9));
    return { car, k, modalites, effs, total: effs.reduce((a, b) => a + b, 0) };
};

/** Bâtons du dépouillement, groupés par cinq. */
const batons = (n) => {
    const paquets = [];
    for (let i = 0; i < n; i += 5) paquets.push('|'.repeat(Math.min(5, n - i)));
    return paquets.join(' ');
};

const introReleve = (d) => `<p>Un professeur a demandé à chaque élève de sa classe ${d.car.sujet}. `
    + 'Il a noté les réponses au fur et à mesure, un bâton par élève.</p>'
    + tableEnonce(...d.modalites.map((m, i) => ligneTable(m, [batons(d.effs[i])])));

const paramsReleve = (d, extra = {}) => ({
    niveau: 1,
    representation_a_produire: 'tableau',
    support_de_production: 'papier',
    nombre_de_categories: d.k,
    effectif_d_une_categorie: d.effs,
    effectif_total: d.total,
    ...extra,
});

const genReleveTrous = () => {
    const d = tirerReleve();
    const phrase = `${d.modalites.map((m, i) => `${m} : {{${d.effs[i]}}}`).join('\n')}\nTotal : {{${d.total}}}`;
    const payload = makeTexteTrou(phrase,
        `${introReleve(d)}<p>Produis le tableau d'effectifs : compte les bâtons de chaque réponse, `
        + 'puis calcule le total de la classe.</p>',
        { ...opts(L_RELEVE), mode: 'saisie', strict: false });
    payload._meta.params = paramsReleve(d, { nombre_de_trous: d.k + 1 });
    return payload;
};

const genReleveQcm = () => {
    const d = tirerReleve();
    const bonne = `Première ligne « ${d.car.entete} », seconde ligne « ${d.car.effLabel} »`;
    const dist = [
        { code: 'tableau_sans_entetes', valeur: 'Aucun en-tête : une seule ligne de nombres suffit' },
        { code: 'tableau_sans_entetes', valeur: `Première ligne « ${d.car.entete} », seconde ligne sans nom` },
        { code: 'tableau_sans_entetes', valeur: 'Première ligne « Nombres », seconde ligne « Nombres »' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introReleve(d)}<p>Tu recopies ces résultats dans un tableau à deux lignes. `
        + 'Comment nommer les en-têtes pour que chaque nombre se rattache à une grandeur ?</p>',
        opts(L_RELEVE));
    payload._meta.params = paramsReleve(d, { distracteurs: dist });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les courbes de températures » (niveau 4 — graphique cartésien)
// ═════════════════════════════════════════════════════════════

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

/** Maximales PAIRES de 12 à 22 °C (unité 2 °C par carreau, 6 à 11 carreaux) ;
 *  minimales PAIRES de 2 à 10 °C (unité 1 °C par carreau, maximum ≥ 6). */
const tirerTemperatures = () => {
    const maximales = JOURS.map(() => 2 * randInt(6, 11));
    const minimales = JOURS.map(() => 2 * randInt(1, 5));
    if (Math.max(...minimales) < 6) minimales[randInt(0, JOURS.length - 1)] = 2 * randInt(3, 5);
    return { maximales, minimales };
};

const tableTemp = (d) => tableEnonce(
    ligneTable('Jour (n°)', JOURS.map((j, i) => `${j} (${i + 1})`)),
    ligneTable('Maximale (°C)', d.maximales),
    ligneTable('Minimale (°C)', d.minimales),
);

const paramsTemp = (d, serie, extra = {}) => {
    const unite = serie === 'maximales' ? 2 : 1;
    const temps = serie === 'maximales' ? d.maximales : d.minimales;
    return {
        niveau: 4,
        representation_a_produire: 'graphique cartésien',
        nombre_de_couples_du_graphique_cartesien: JOURS.length,
        unite_de_l_axe_des_valeurs: unite,
        hauteur_max_en_carreaux: Math.max(...temps) / unite,
        hauteur_min_en_carreaux: Math.min(...temps) / unite,
        ...extra,
    };
};

const genTempQcm = () => {
    const d = tirerTemperatures();
    const bonne = 'Les jours en abscisse (axe horizontal), les températures en ordonnée (axe vertical)';
    const dist = [
        { code: 'abscisse_ordonnee_inversees', valeur: 'Les températures en abscisse (axe horizontal), les jours en ordonnée (axe vertical)' },
        { code: 'abscisse_ordonnee_inversees', valeur: 'Les deux grandeurs sur le même axe' },
        { code: 'abscisse_ordonnee_inversees', valeur: 'Peu importe : le graphique sera le même dans tous les cas' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        '<p>Météo France annonce les températures maximales et minimales des 7 prochains jours. '
        + 'On veut représenter ces prévisions par un graphique cartésien.</p>'
        + tableTemp(d)
        + '<p>Quelles grandeurs faut-il placer sur les axes ?</p>',
        opts(L_TEMP));
    payload._meta.params = paramsTemp(d, 'maximales', { distracteurs: dist });
    return payload;
};

const genTempPlacer = () => {
    const d = tirerTemperatures();
    const maxi = randInt(0, 1) === 0;
    const serie = maxi
        ? { cle: 'maximales', nom: 'maximales', temps: d.maximales, unite: 2, yMax: 24 }
        : { cle: 'minimales', nom: 'minimales', temps: d.minimales, unite: 1, yMax: 12 };
    // 4 jours à placer, 3 déjà placés (règle 3-6 points du format) : les points
    // déjà posés ne sont JAMAIS ceux qui sont demandés.
    const indices = shuffle([0, 1, 2, 3, 4, 5, 6]);
    const aPlacer = indices.slice(0, 4).sort((a, b) => a - b);
    const dejaIdx = indices.slice(4).sort((a, b) => a - b);
    const points = aPlacer.map((i) => ({ label: `J${i + 1}`, x: i + 1, y: serie.temps[i] }));
    const dejaPlaces = dejaIdx.map((i) => ({ x: i + 1, y: serie.temps[i], label: `J${i + 1}` }));
    const texte = '<p>Voici les prévisions de températures de la semaine :</p>'
        + tableTemp(d)
        + `<p>On représente les températures ${serie.nom} par un graphique cartésien : l'axe vertical `
        + `est gradué de ${serie.unite} en ${serie.unite} °C. Les points des jours `
        + `${dejaIdx.map((i) => i + 1).join(', ')} sont déjà placés. Place les points manquants : pour `
        + `chaque jour, abscisse = numéro du jour, ordonnée = température ${serie.nom.slice(0, -1)}.</p>`;
    const payload = makePlacer2D(texte,
        { xMax: 8, yMax: serie.yMax, xStep: 1, yStep: serie.unite, xLabel: 'Jour (n°)', yLabel: 'Température (°C)', grille: true },
        points, L_TEMP, dejaPlaces);
    payload._meta.params = paramsTemp(d, serie.cle, {
        support_de_production: 'papier',
        erreurVisee: 'abscisse_ordonnee_inversees',
        hauteur_d_une_barre_en_carreaux: serie.temps.map((t) => t / serie.unite),
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le stock de canapés » (niveaux 2 et 3)
// ═════════════════════════════════════════════════════════════

const TYPES_CANAPES = ['2 places', '3 places', 'Clic-clac', 'BZ'];

/** Total divisant 360 (angles entiers), type dominant au-delà de la moitié,
 *  effectifs DISTINCTS, tous supérieurs à l'unité de l'axe (au moins un
 *  carreau), la plus grande barre occupant 6 à 12 carreaux. */
const tirerCanapes = () => {
    for (let essai = 0; essai < 200; essai++) {
        const total = randomChoice([72, 90, 120, 180]);
        const unite = total >= 120 ? 10 : 5;
        const lo = Math.max(Math.ceil(total * 0.06), unite);
        const gros = randInt(Math.ceil(total * 0.52), Math.floor(total * 0.6));
        const e1 = randInt(lo, Math.floor(total * 0.2));
        const e2 = randInt(lo, Math.floor(total * 0.2));
        const e3 = total - gros - e1 - e2;
        if (e3 < lo || e3 > Math.floor(total * 0.25)) continue;
        const effs = [e1, e2, gros, e3];
        if (new Set(effs).size !== 4) continue;
        const hauteurs = effs.map((e) => e / unite);
        if (Math.max(...hauteurs) < 6 || Math.max(...hauteurs) > 12) continue;
        return { total, effs, degPer: 360 / total, unite, hauteurs };
    }
    return { total: 90, effs: [18, 14, 47, 11], degPer: 4, unite: 5, hauteurs: [3.6, 2.8, 9.4, 2.2] };
};

const tableCanapes = (d) => tableEnonce(
    ligneTable('Type de canapé', TYPES_CANAPES),
    ligneTable('Effectif', d.effs),
);

const INTRO_CANAPES = '<p>Un magasin fait l\'inventaire de son stock de canapés.</p>';

const paramsCanapes = (d, niveau, extra = {}) => ({
    niveau,
    nombre_de_categories: TYPES_CANAPES.length,
    effectif_total_compatible_avec_des_angles_entiers: d.total,
    effectif_d_une_categorie: d.effs,
    angle_d_un_secteur: d.effs.map((e) => e * d.degPer),
    ...extra,
});

const genCanapesTrous = () => {
    // Chaîne complète de construction du diagramme circulaire : le total et la
    // valeur d'un canapé en degrés sont les deux premiers trous, puis les
    // angles, puis la vérification (la somme fait un tour complet).
    const d = tirerCanapes();
    const angles = d.effs.map((e) => e * d.degPer);
    const phrase = `Total du stock : {{${d.total}}} canapés\n`
        + `Un canapé, soit 360° divisé par le total : {{${d.degPer}}}°\n`
        + `${TYPES_CANAPES[1]} : {{${angles[1]}}}°\n`
        + `${TYPES_CANAPES[2]} : {{${angles[2]}}}°\n`
        + `${TYPES_CANAPES[3]} : {{${angles[3]}}}°\n`
        + `Somme des quatre angles : {{360}}°`;
    const texte = `${INTRO_CANAPES}${tableCanapes(d)}`
        + '<p>Tu vas tracer le diagramme circulaire de ce stock. Calcule d\'abord le total, puis la '
        + 'valeur en degrés d\'un seul canapé (le stock complet correspond à 360°), puis l\'angle de '
        + `chaque secteur (effectif × valeur d'un canapé). L'angle de « ${TYPES_CANAPES[0]} » est `
        + `${angles[0]}° : vérifie à la fin que la somme des quatre angles fait un tour complet.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CANAPES), mode: 'saisie', strict: false });
    payload._meta.params = paramsCanapes(d, 3, {
        representation_a_produire: 'diagramme circulaire',
        support_de_production: 'papier',
        erreurVisee: 'total_faux_pour_les_angles',
        nombre_de_trous: 6,
    });
    return payload;
};

/** Permutations visuellement distinctes d'effectifs distincts. */
const permutations = (effs) => {
    const swapMaxMin = [...effs];
    const iMax = effs.indexOf(Math.max(...effs));
    const iMin = effs.indexOf(Math.min(...effs));
    [swapMaxMin[iMax], swapMaxMin[iMin]] = [swapMaxMin[iMin], swapMaxMin[iMax]];
    const cycle = effs.map((_, i) => effs[(i + 1) % effs.length]);
    const cycle2 = effs.map((_, i) => effs[(i + 2) % effs.length]);
    return [swapMaxMin, cycle, cycle2];
};

const genCanapesQcmImages = () => {
    const d = tirerCanapes();
    const enBarres = randInt(0, 1) === 0;
    const fabrique = enBarres
        ? (effs) => imgChoix(svgBarres(TYPES_CANAPES, effs, { yStep: d.unite, yLabel: 'Effectifs', compact: true }))
        : (effs) => imgChoix(svgCamembert(
            TYPES_CANAPES.map((nom, i) => ({ nom, pct: (effs[i] * 100) / d.total })),
            { compact: true }));
    const bonneChoix = fabrique(d.effs);
    const dist = permutations(d.effs).map((effs) => ({ code: 'effectifs_permutes', valeur: fabrique(effs) }));
    const payload = makeQcm([bonneChoix, ...dist.map((x) => x.valeur)], bonneChoix,
        `${INTRO_CANAPES}${tableCanapes(d)}`
        + `<p>Quel diagramme ${enBarres ? 'en barres' : 'circulaire'} correspond à ce stock ? `
        + (enBarres
            ? `L'axe des effectifs est gradué de ${d.unite} en ${d.unite}. `
            : `(Aide : « ${TYPES_CANAPES[2]} » représente plus de la moitié du stock, son secteur doit dépasser un demi-disque.) `)
        + 'Les types sont toujours dans l\'ordre du tableau.</p>',
        opts(L_CANAPES));
    payload._meta.params = paramsCanapes(d, enBarres ? 2 : 3, {
        representation_a_produire: enBarres ? 'diagramme en barres' : 'diagramme circulaire',
        ...(enBarres ? {
            unite_de_l_axe_des_valeurs: d.unite,
            hauteur_d_une_barre_en_carreaux: d.hauteurs,
            hauteur_max_en_carreaux: Math.max(...d.hauteurs),
            hauteur_min_en_carreaux: Math.min(...d.hauteurs),
        } : {}),
        distracteurs: dist.map((x) => ({ code: x.code })),
    });
    return payload;
};

const genCanapesVF = () => {
    const d = tirerCanapes();
    const angles = d.effs.map((e) => e * d.degPer);
    const totalPartiel = d.total - d.effs[3];            // la dernière catégorie oubliée
    const angleFaux = Math.round((d.effs[2] * 360) / totalPartiel);
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('total_faux_pour_les_angles');
    if (!vs[1]) codes.push('total_faux_pour_les_angles');
    if (!vs[3]) codes.push('legende_oubliee');
    const lignes = [
        [`Le secteur « ${TYPES_CANAPES[2]} » mesure ${vs[0] ? angles[2] : angleFaux}°.`, vs[0] ? 0 : 1],
        [`Pour calculer les angles, on partage 360° selon le total de ${vs[1] ? d.total : totalPartiel} canapés.`, vs[1] ? 0 : 1],
        [`La somme des quatre angles du diagramme vaut ${vs[2] ? 360 : 350}°.`, vs[2] ? 0 : 1],
        [vs[3]
            ? 'Sans légende, on ne peut pas savoir quel secteur correspond à quel type de canapé.'
            : 'La légende est inutile : les couleurs suffisent à reconnaître chaque type de canapé.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${INTRO_CANAPES}${tableCanapes(d)}<p>Tu construis le diagramme circulaire de ce stock. Vrai ou faux ?</p>`,
        opts(L_CANAPES));
    payload._meta.params = paramsCanapes(d, 3, {
        representation_a_produire: 'diagramme circulaire',
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les précipitations de Brest » (niveaux 2 et 4)
// ═════════════════════════════════════════════════════════════

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const UNITE_BREST = 20;

/** Précipitations multiples de 20 mm : chaque bâton occupe un nombre ENTIER de
 *  carreaux (1 à 12) et le plus haut en occupe au moins 6. */
const tirerBrest = () => {
    const semestre = randInt(0, 1);
    const noms = MOIS.slice(semestre * 6, semestre * 6 + 6);
    const valeurs = noms.map(() => UNITE_BREST * randInt(1, 12));
    if (Math.max(...valeurs) < 6 * UNITE_BREST) {
        valeurs[randInt(0, 5)] = UNITE_BREST * randInt(6, 12);
    }
    return { semestre, noms, valeurs };
};

const tableBrest = (d) => tableEnonce(
    ligneTable('Mois (n°)', d.noms.map((n, i) => `${n} (${i + 1})`)),
    ligneTable('Précipitations (mm)', d.valeurs),
);

const paramsBrest = (d, niveau, extra = {}) => ({
    niveau,
    representation_a_produire: 'diagramme en barres',
    nombre_de_categories: d.noms.length,
    nombre_de_couples_du_graphique_cartesien: d.noms.length,
    unite_de_l_axe_des_valeurs: UNITE_BREST,
    hauteur_d_une_barre_en_carreaux: d.valeurs.map((v) => v / UNITE_BREST),
    hauteur_max_en_carreaux: Math.max(...d.valeurs) / UNITE_BREST,
    hauteur_min_en_carreaux: Math.min(...d.valeurs) / UNITE_BREST,
    ...extra,
});

const genBrestPlacer = () => {
    const d = tirerBrest();
    const labels = d.noms.map((n) => `${n.slice(0, 3)}.`);
    const points = d.noms.map((n, i) => ({ label: labels[i], x: i + 1, y: d.valeurs[i] }));
    const texte = `<p>Voici les précipitations mensuelles relevées à Brest au ${d.semestre === 0 ? 'premier' : 'second'} semestre 2020 :</p>`
        + tableBrest(d)
        + `<p>On construit le diagramme en bâtons : l'axe vertical est gradué de ${UNITE_BREST} en `
        + `${UNITE_BREST} mm, soit ${UNITE_BREST} mm par carreau. Place le sommet de chaque bâton, `
        + 'un point par mois : abscisse = numéro du mois, ordonnée = précipitations.</p>';
    const payload = makePlacer2D(texte,
        { xMax: 7, yMax: 240, xStep: 1, yStep: UNITE_BREST, xLabel: 'Mois (n°)', yLabel: 'Précipitations (mm)', grille: true },
        points, L_BREST);
    payload._meta.params = paramsBrest(d, 2, { support_de_production: 'papier' });
    return payload;
};

const genBrestQcm = () => {
    const d = tirerBrest();
    const variante = randInt(0, 2);
    if (variante === 0) {
        // Une valeur dépasse la graduation prévue : il faut refaire l'axe.
        const yMaxAffiche = 280;
        const gros = randomChoice([300, 320, 340]);
        const valeurs = [...d.valeurs];
        valeurs[5] = gros;
        const svg = svgBarres(d.noms.map((n) => `${n.slice(0, 3)}.`), valeurs,
            { yStep: UNITE_BREST, yMax: yMaxAffiche, yLabel: 'Précipitations (mm)', xLabel: 'Mois' });
        const bonne = `Refaire le diagramme avec un axe gradué au-delà de ${gros} mm`;
        const dist = [
            { code: 'echelle_axe_mal_choisie', valeur: 'Couper la barre : on garde l\'axe et la barre s\'arrête en haut du cadre' },
            { code: 'echelle_axe_mal_choisie', valeur: `Ignorer le mois de ${d.noms[5]} : on ne le représente pas` },
            { code: 'echelle_axe_mal_choisie', valeur: `Remplacer la valeur par ${yMaxAffiche} mm pour qu'elle rentre dans le cadre` },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `<p>On a tracé le diagramme des précipitations avec un axe gradué jusqu'à ${yMaxAffiche} mm. `
            + `Or il est tombé ${gros} mm en ${d.noms[5]} : la barre dépasse la graduation.</p>`
            + '<p>Que faut-il faire pour représenter correctement toutes les données ?</p>',
            opts(L_BREST));
        payload.elements_additionnels = imgElement(svg);
        payload._meta.params = paramsBrest(d, 4, { distracteurs: dist.map((x) => ({ code: x.code })) });
        return payload;
    }
    if (variante === 1) {
        const maxV = Math.max(...d.valeurs);
        const bonne = `De 0 à ${maxV + UNITE_BREST} mm, de ${UNITE_BREST} en ${UNITE_BREST}`;
        const dist = [
            { code: 'echelle_axe_mal_choisie', valeur: `De 0 à ${maxV - 3 * UNITE_BREST} mm, de ${UNITE_BREST} en ${UNITE_BREST}` },
            { code: 'echelle_axe_mal_choisie', valeur: 'De 0 à 10 mm, de 1 en 1' },
            { code: 'echelle_axe_mal_choisie', valeur: `De ${Math.min(...d.valeurs)} à ${maxV} mm : on ne gradue qu'entre les valeurs` },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `<p>On veut représenter ces précipitations par un diagramme en bâtons :</p>${tableBrest(d)}`
            + '<p>Quelle graduation choisir pour l\'axe vertical afin que tous les bâtons soient visibles '
            + 'en entier et que les hauteurs soient comparables (axe partant de 0) ?</p>',
            opts(L_BREST));
        payload._meta.params = paramsBrest(d, 4, { distracteurs: dist.map((x) => ({ code: x.code })) });
        return payload;
    }
    // Effectif → hauteur : diviser par l'unité de l'axe annoncée.
    const i = randInt(0, 5);
    const v = d.valeurs[i];
    const bonne = `${v / UNITE_BREST} carreaux`;
    const dist = [
        { code: 'effectif_brut_en_hauteur', valeur: `${v} carreaux` },
        { code: 'effectif_brut_en_hauteur', valeur: `${v * UNITE_BREST} carreaux` },
        { code: 'effectif_brut_en_hauteur', valeur: `${UNITE_BREST} carreaux` },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `<p>On trace le diagramme en bâtons de ces précipitations sur du papier quadrillé :</p>${tableBrest(d)}`
        + `<p>L'axe vertical est gradué de ${UNITE_BREST} en ${UNITE_BREST} mm : un carreau vaut `
        + `${UNITE_BREST} mm. Quelle doit être la hauteur du bâton de ${d.noms[i]} `
        + `(${v} mm) ?</p>`,
        opts(L_BREST));
    payload._meta.params = paramsBrest(d, 2, {
        support_de_production: 'papier',
        distracteurs: dist.map((x) => ({ code: x.code })),
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le régime du sanglier » (niveau 3 — diagramme circulaire)
// ═════════════════════════════════════════════════════════════

const ALIMENTS = ['Fruits forestiers', 'Graminées', 'Céréales', 'Animaux', 'Racines'];

/** Pourcentages multiples de 5 (angles entiers, aucun secteur sous 10°),
 *  fruits au-delà de 50 % (secteur au-delà de 180°), somme 100. */
const tirerSanglier = () => {
    for (let essai = 0; essai < 80; essai++) {
        const fruits = randomChoice([55, 60, 65]);
        const gram = randomChoice([10, 15, 20]);
        const cer = randomChoice([5, 10, 15]);
        const ani = 5;
        const rac = 100 - fruits - gram - cer - ani;
        if (rac < 5 || rac > 20) continue;
        return { pcts: [fruits, gram, cer, ani, rac] };
    }
    return { pcts: [60, 15, 10, 5, 10] };
};

const angleDe = (pct) => (pct * 18) / 5;   // pct multiple de 5 → angle entier

const tableSanglier = (d, masque) => tableEnonce(
    ligneTable('Aliment', [...ALIMENTS, 'Total']),
    ligneTable('Pourcentage', [...d.pcts.map((p, i) => (masque === 'pct' && i > 0 ? '?' : `${p} %`)), '100 %']),
    ligneTable('Angle', [...d.pcts.map((p, i) => (masque === 'angle' && i > 0 ? '?' : `${angleDe(p)}°`)), '360°']),
);

const INTRO_SANGLIER = '<p>Une étude décrit le régime alimentaire du sanglier. '
    + 'On veut le représenter par un diagramme circulaire.</p>';

const paramsSanglier = (d, extra = {}) => ({
    niveau: 3,
    representation_a_produire: 'diagramme circulaire',
    nombre_de_categories: ALIMENTS.length,
    angle_d_un_secteur: d.pcts.map(angleDe),
    ...extra,
});

const genSanglierTrous = () => {
    const d = tirerSanglier();
    if (randInt(0, 1) === 0) {
        // Pourcentage → angle (l'exemple des fruits est donné dans le tableau).
        const phrase = ALIMENTS.slice(1).map((a, k) => `${a} : {{${angleDe(d.pcts[k + 1])}}}°`).join('\n');
        const texte = `${INTRO_SANGLIER}${tableSanglier(d, 'angle')}`
            + '<p>100 % correspond à 360° : chaque 5 % vaut donc 18°. '
            + `Par exemple, « ${ALIMENTS[0]} » : ${d.pcts[0]} % donne ${angleDe(d.pcts[0])}°. `
            + 'Complète les angles manquants, puis trace les secteurs au rapporteur.</p>';
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_SANGLIER), mode: 'saisie', strict: false });
        payload._meta.params = paramsSanglier(d, { support_de_production: 'papier', sens: 'pourcentage vers angle', nombre_de_trous: 4 });
        return payload;
    }
    // Angle → pourcentage (le sens inverse).
    const phrase = ALIMENTS.slice(1).map((a, k) => `${a} : {{${d.pcts[k + 1]}}} %`).join('\n');
    const texte = `${INTRO_SANGLIER}${tableSanglier(d, 'pct')}`
        + '<p>360° correspond à 100 % : chaque 18° vaut donc 5 %. '
        + `Par exemple, « ${ALIMENTS[0]} » : ${angleDe(d.pcts[0])}° donne ${d.pcts[0]} %. `
        + 'Complète les pourcentages manquants.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_SANGLIER), mode: 'saisie', strict: false });
    payload._meta.params = paramsSanglier(d, { support_de_production: 'papier', sens: 'angle vers pourcentage', nombre_de_trous: 4 });
    return payload;
};

const genSanglierQcm = () => {
    const d = tirerSanglier();
    const parts = ALIMENTS.map((nom, i) => ({ nom, pct: d.pcts[i] }));
    const bonneChoix = imgChoix(svgCamembert(parts, { compact: true }));
    const oublie = parts.slice(0, ALIMENTS.length - 1);        // une catégorie oubliée : le disque n'est pas complet
    const permute = ALIMENTS.map((nom, i) => ({ nom, pct: d.pcts[(i + 1) % ALIMENTS.length] }));
    const dist = [
        { code: 'legende_oubliee', valeur: imgChoix(svgCamembert(parts, { compact: true, legende: false })) },
        { code: 'total_faux_pour_les_angles', valeur: imgChoix(svgCamembert(oublie, { compact: true })) },
        { code: 'effectifs_permutes', valeur: imgChoix(svgCamembert(permute, { compact: true })) },
    ];
    const payload = makeQcm([bonneChoix, ...dist.map((x) => x.valeur)], bonneChoix,
        `${INTRO_SANGLIER}${tableSanglier(d, null)}`
        + `<p>Quel diagramme circulaire as-tu produit correctement ? Rappel : « ${ALIMENTS[0]} » `
        + `mesure ${angleDe(d.pcts[0])}°, donc plus de la moitié du disque, et la somme des cinq `
        + 'secteurs doit faire un tour complet.</p>',
        opts(L_SANGLIER));
    payload._meta.params = paramsSanglier(d, { distracteurs: dist.map((x) => ({ code: x.code })) });
    return payload;
};

const svgSanglier = (d) => imgElement(svgCamembert(
    ALIMENTS.map((nom, i) => ({ nom, pct: d.pcts[i] })), {}));

const genSanglierVF = () => {
    const d = tirerSanglier();
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[1]) codes.push('angle_depuis_pourcentage_arrondi');
    if (!vs[2]) codes.push('total_faux_pour_les_angles');
    const lignes = [
        [`Le secteur « ${ALIMENTS[1]} » mesure ${vs[0] ? angleDe(d.pcts[1]) : d.pcts[1]}°.`, vs[0] ? 0 : 1],
        [vs[1]
            ? 'Si on arrondit les pourcentages avant de calculer les angles, la somme des angles peut ne plus faire exactement 360°.'
            : 'Si on arrondit les pourcentages avant de calculer les angles, la somme des angles fait toujours exactement 360°.', vs[1] ? 0 : 1],
        [`La somme des cinq angles du diagramme vaut ${vs[2] ? 360 : 300}°.`, vs[2] ? 0 : 1],
        [`Les fruits forestiers représentent ${vs[3] ? 'plus' : 'moins'} de la moitié du régime : `
            + `${angleDe(d.pcts[0])}° face aux 180° du demi-disque.`, vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${INTRO_SANGLIER}${tableSanglier(d, null)}<p>Vrai ou faux ?</p>`,
        { ...opts(L_SANGLIER), elements_additionnels: svgSanglier(d) });
    payload._meta.params = paramsSanglier(d, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    releve_texte_trou_saisie: genReleveTrous,
    releve_qcm_unique: genReleveQcm,
    brest_placer_points_2d: genBrestPlacer,
    brest_qcm_unique: genBrestQcm,
    canapes_texte_trou_saisie: genCanapesTrous,
    canapes_qcm_unique: genCanapesQcmImages,
    canapes_tableau_vf: genCanapesVF,
    sanglier_texte_trou_saisie: genSanglierTrous,
    sanglier_qcm_unique: genSanglierQcm,
    sanglier_tableau_vf: genSanglierVF,
    temp_qcm_unique: genTempQcm,
    temp_placer_points_2d: genTempPlacer,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
    placer_points_2d: 'Placer (repère)',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'releve', label: L_RELEVE, formatsList: buildFormats('releve') },
    { id: 'brest', label: L_BREST, formatsList: buildFormats('brest') },
    { id: 'canapes', label: L_CANAPES, formatsList: buildFormats('canapes') },
    { id: 'sanglier', label: L_SANGLIER, formatsList: buildFormats('sanglier') },
    { id: 'temp', label: L_TEMP, formatsList: buildFormats('temp') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
